import React, { ReactElement, useMemo } from 'react';
import { useTheme } from '@mui/material';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CurveProps,
} from 'recharts';
import { AxisDomain, BaseAxisProps } from 'recharts/types/util/types';

import { useStyles } from './ChartStyles';
import { MARGIN } from './ChartUtils';
import { useYAxisWidth } from './useYAxisWidth';

export interface IChartData {
  time: Date;
  value?: string | number;
  extraValue?: string | number;
  shouldShowZeroValues?: boolean;
}

export interface IChartProps {
  data: IChartData[];
  tooltipContent?: ReactElement;
  xAxisTickFormatter?: BaseAxisProps['tickFormatter'];
  yAxisTickFormatter?: BaseAxisProps['tickFormatter'];
  yAxisDomain?: AxisDomain; // [0, 2700000] min/max values for Y axis
  loading?: boolean;
  height?: number;
  hasFixedHeight?: boolean;
  hasHorizontalLines?: boolean;
  width?: string | number;
  isDisabledColor?: boolean;
  type?: CurveProps['type'];
}

const ANIMATION_DURATION = 500;
const FIXED_HEIGHT = 270;
const WIDTH = '99%';

export const Chart = ({
  data,
  tooltipContent,
  xAxisTickFormatter,
  yAxisTickFormatter,
  loading,
  height = FIXED_HEIGHT,
  hasFixedHeight = true,
  hasHorizontalLines = true,
  width = WIDTH,
  isDisabledColor,
  yAxisDomain,
  type = 'monotone',
}: IChartProps) => {
  const yAxisWidth = useYAxisWidth(data, yAxisTickFormatter);

  const theme = useTheme();
  const { classes, cx } = useStyles();

  const strokeColor = useMemo(() => {
    if (isDisabledColor) {
      return theme.palette.grey[400];
    }

    return theme.palette.primary.main;
  }, [isDisabledColor, theme]);

  return (
    <ResponsiveContainer
      className={classes.root}
      width={width}
      height={hasFixedHeight ? height : undefined}
    >
      <AreaChart
        width={0}
        className={cx(classes.chart, loading ? classes.loading : null)}
        data={data}
        margin={MARGIN}
      >
        <defs>
          <linearGradient id="valueColor" x1="0" y1="0" x2="0" y2="1" />
        </defs>
        <defs>
          <linearGradient id="extraValueColor" x1="0" y1="0" x2="0" y2="1" />
        </defs>
        <CartesianGrid
          style={{ stroke: theme.palette.grey[100] }}
          strokeWidth={1}
          horizontal={hasHorizontalLines}
          vertical
          strokeDasharray="5 5"
        />
        <XAxis
          dataKey="time"
          tickLine
          tick={{ fill: theme.palette.text.secondary, dy: 10 }}
          stroke=""
          tickFormatter={xAxisTickFormatter}
        />
        <YAxis
          tickLine={false}
          tick={{ fill: theme.palette.text.secondary, dx: -5 }}
          stroke=""
          tickFormatter={yAxisTickFormatter}
          width={yAxisWidth}
          domain={yAxisDomain}
        />
        {tooltipContent && <Tooltip content={tooltipContent} />}
        <Area
          dataKey="value"
          stroke={strokeColor}
          strokeWidth={2}
          fill="url(#valueColor)"
          type={type}
          animationDuration={ANIMATION_DURATION}
        />
        <Area
          dataKey="extraValue"
          stroke={strokeColor}
          strokeWidth={2}
          fill="url(#extraValueColor)"
          strokeDasharray="3"
          type={type}
          animationDuration={ANIMATION_DURATION}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};
